import _ from 'lodash';
import { formatUser } from '~/utils/format-user'

export const getters = () => ({
  accessToken: state => state.accessToken,
  jwt: state => state.jwt,
  authenticatedUser: state => state.user,
  isAuthenticated: state => state.isAuthenticated,
  authReady: state => state.authReady,
  authenticationStatus: state => {
    return state.authenticationStatus
      ? state.authenticationStatus
      : { variant: "secondary" };
  },
  hasAuthenticationStatus: state => {
    return !!state.authenticationStatus;
  }
})
export const state = () => ({
  accessToken: null,
  refreshToken: null,
  jwt: null,
  loginToken: null,
  sessionToken: null,
  token: null,
  user: null,
  isAuthenticated: false,
  authenticating: false,
  authReady: false,
  authenticationStatus: null,
})

export const mutations = {
  setAuthReady(state, status) {
    state.authReady = status;
  },
  setAuthenticationError(state, err) {
    state.authenticationStatus = {
      state: "failed",
      message: err,
      variant: "danger"
    };
  },
  clearAuthenticationStatus: state => {
    state.authenticationStatus = null;
  },  
  setSessionToken: (state, value) => {
    state.sessionToken = value;
  },  
  setLoginToken: (state, value) => {
    state.loginToken = value;
  },
  setJwt: (state, value) => {
    state.jwt = value;
  },  
  storeToken: (state, value) => {
    state.token = value;
  },
  storeAccessToken: (state, value) => {
    state.accessToken = value;
  },
  setAuthenticated: (state, value) => {
    state.isAuthenticated = value;
    if(!value) {
      state.user = null;
    }
  },
  storeUser(state, value) {
    if(_.isObject(state.user)) {
      state.user = _.merge(state.user, value);
    } else {
      state.user = value;
    }
    state.user = _.clone(state.user)
  }, 
  setUser(state, user) {
    if (!user) {
      state.user = null
      state.isAuthenticated = false
      return
    }

    state.user = _.isObject(state.user)
        ? _.cloneDeep(_.merge({}, state.user, user))
        : _.cloneDeep(user)

    state.isAuthenticated = true
  },  
  setAuthenticating(state, status) {
    console.log('setAuthenticating', status);
    state.authenticating = status;
  },
  clearAuthentication(state) {
    state.jwt = null;
    state.user = null;
    state.userId = null;
    state.isAuthenticated = false;
  },
  setFavoriteSports(state, ids) {
    if (!state.user) return
    const normalized = (ids || []).map(Number)
    state.user = { ...state.user, favorite_sports: normalized }
  },
  addFavoriteSport(state, id) {
    if (!state.user) return
    const nid = Number(id)
    const list = Array.isArray(state.user.favorite_sports)
      ? state.user.favorite_sports.map(Number)
      : []
    if (!list.includes(nid)) list.push(nid)
    state.user = { ...state.user, favorite_sports: list }
  },
  removeFavoriteSport(state, id) {
    if (!state.user) return
    const nid = Number(id)
    const list = Array.isArray(state.user.favorite_sports)
      ? state.user.favorite_sports.map(Number).filter(x => x !== nid)
      : []
    state.user = { ...state.user, favorite_sports: list }
  },
  addFavoriteCompetition(state, { sportKey, id }) {
    if (!state.user) return

    const key = String(sportKey ?? '').trim().toLowerCase()
    const dict = { ...(state.user.favorite_competitions || {}) }
    const current = Array.isArray(dict[key]) ? dict[key] : []
    const set = new Set(current.map(Number))

    set.add(Number(id))
    dict[key] = Array.from(set)

    state.user = { ...state.user, favorite_competitions: dict }
  },
  removeFavoriteCompetition(state, { sportKey, id }) {
    if (!state.user) return
    
    const key = String(sportKey ?? '').trim().toLowerCase()
    const dict = { ...(state.user.favorite_competitions || {}) }
    const current = Array.isArray(dict[key]) ? dict[key].map(Number) : []
    dict[key] = current.filter(x => x !== Number(id))

    state.user = { ...state.user, favorite_competitions: dict }
  },
}

export const actions = {
  async initAuth(context, authClient) {
    // Persisted authReady can be true from a previous logged-out visit.
    // Keep the header on its placeholder until the cookie session is known,
    // otherwise Login/Join flash for a few seconds after a casino → sport switch.
    context.commit('setAuthReady', false)

    if (context.state.user) {
      context.commit('setAuthReady', true)
      return
    }

    try {
      // Same client as checkLogin: AUTH_API with credentials.
      // The default axios client misses the cross-site session cookie.
      const auth = authClient || this.$auth
      const response = await auth().get('/api/common/check-login', {})
      const data = _.get(response, 'data')

      if (_.get(data, 'result')) {
        const user_info = formatUser(_.get(data, 'userInfo'))
        if (user_info && user_info.require_verify === false) {
          user_info.balance = user_info.balance * 100
          if (process.client) {
            localStorage.setItem('jwt', _.get(data, 'jwt'))
            localStorage.setItem('login_token', _.get(data, 'userInfo.login_token'))
          }
          context.commit('setLoginToken', _.get(data, 'userInfo.login_token'))
          context.commit('setJwt', _.get(data, 'jwt'))
          context.commit('storeUser', user_info)
        }
      } else if (_.has(data, 'result')) {
        context.commit('clearAuthentication')
      }
    } catch(e) {
      console.error('initAuth failed:', e)
      if (_.has(e, 'response.data.result') && !_.get(e, 'response.data.result')) {
        context.commit('clearAuthentication')
      }
    } finally {
      context.commit('setAuthReady', true)
    }
  },
  async initAuth1(context) {
    if (context.state.authReady) return;

    try {
      await context.dispatch('currentUser');
    } catch (e) {
      context.commit('setUser', null);
    } finally {
      context.commit('setAuthReady', true);
    }
  },
  async setTokens(context, params) {
    console.log('setTokens')
  },
  clearAuthenticationStatus: context => {
    context.commit("clearAuthenticationStatus", null);
  },
  async currentUser(context, force = false) {
    try {
      let response = await this.$sportify().auth.currentUser(force)
      console.log('currentUser', response);
      context.commit("setUser", response);
    } catch(e) {
      context.commit("setUser", null);
    }
  },
  async refreshBalance(context, force = false) {
    try {
      let myUser = await this.$sportify().auth.getUser(force)
      if(myUser) {
        await this.$sportify().auth.storeUser({balance: myUser.balance})
      }
      let user = await this.$sportify().auth.currentUser()

      console.log('refreshBalance', user);
      context.commit("setUser", user);
    } catch(e) {
      //context.commit("setUser", null);
    }
  },
  async login(context, params) {
    console.log('login', context, params);
    context.commit("auth/clearAuthenticationStatus", null, { root: true });
    context.commit("setAuthenticating", true);
    try {
      
      let response = await this.$sportify().auth.login(params.username, params.password, params)

      let userData =  _.get(response, 'data')
      context.commit("setUser", _.get(response, 'data'));
            
    } catch (e) {
      console.log('login setAuthenticationError', e)
      context.commit("auth/setAuthenticationError", _.get(e, 'error_message'), { root: true });
    }
    context.commit("setAuthenticating", false);
  },
  async recover(context, response) {

    try {
      console.log('recover', response)
      let userData =  _.get(response, 'user_metadata')
      userData.email = _.get(response, 'email')
      userData.id = _.get(response, 'id')

      context.commit("setUserAuthenticated", userData);
      context.commit("setRefreshToken", response.token.refresh_token);
      context.commit("setAccessToken", response.token.access_token);

 
    } catch (err) {
    }
  },
  async logout(context, params) {
    console.log('context', context);
    console.log('params', params);
    try {
      const user =  await this.$sportify().auth.logout();
      context.commit("user", null);
    } catch (err) {
      console.log("error during sign out: {}", err);
    }
    context.commit("auth/clearAuthentication", null, { root: true });
    context.commit('auth/clearAuthenticationStatus', null, { root: true });

  },
  signUp: async (context, params) => {
    context.commit("auth/clearAuthenticationStatus", null, { root: true });
    try {
      await Auth.signUp(params);
      context.commit("auth/clearAuthentication", null, { root: true });
    } catch (err) {
      context.commit("auth/setAuthenticationError", err, { root: true });
    }
  },
  confirmSignUp: async (context, params) => {
    context.commit("auth/clearAuthenticationStatus", null, { root: true });
    try {
      await Auth.confirmSignUp(params.username, params.code);
    } catch (err) {
      context.commit("auth/setAuthenticationError", err, { root: true });
    }
  },
  confirmResend: async (context, params) => {
    context.commit("auth/clearAuthenticationStatus", null, { root: true });
    try {
      await Auth.resendSignUp(params.username);
    } catch (err) {
      context.commit("auth/setAuthenticationError", err, { root: true });
    }
  },
  passwordReset: async (context, params) => {
    context.commit("auth/clearAuthenticationStatus", null, { root: true });
    try {
      await Auth.forgotPassword(params.username);
    } catch (err) {
      context.commit("auth/setAuthenticationError", err, { root: true });
    }
  },
  confirmPasswordReset: async (context, params) => {
    context.commit("auth/clearAuthenticationStatus", null, { root: true });
    try {
      await Auth.forgotPasswordSubmit(
        params.username,
        params.code,
        params.password
      );
    } catch (err) {
      context.commit("auth/setAuthenticationError", err, { root: true });
    }
  },
  passwordResetResend: async (context, params) => {
    context.commit("auth/clearAuthenticationStatus", null, { root: true });
    try {
      await Auth.passwordResetResend(params.username);
    } catch (err) {
      context.commit("auth/setAuthenticationError", err, { root: true });
    }
  },
  passwordChange: async (context, params) => {
    context.commit("auth/clearAuthenticationStatus", null, { root: true });
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(
        user,
        params.currentPassword,
        params.newPassword
      );
    } catch (err) {
      context.commit("auth/setAuthenticationError", err, { root: true });
    }
  }
};
